import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  Input,
  Text,
  Textarea,
  useToast,
  FormControl,
  FormLabel,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, getDocs, query, where, doc, updateDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { auth, firestore } from "../firebase/firebaseConfig";
import { fetchUser, fetchUserProfile } from "../services/userService";
import { useAuth } from "../context/AuthProvider";
import { useQuota } from "../context/QuotaContext";
import { useTranslation } from "react-i18next";
import { enforceQuotaAndListingLimits } from "../services/quotaService";

const ListingDetailsModal = ({ isOpen, onClose, listing }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const modalBg = useColorModeValue("whiteAlpha.900", "gray.800");
  const textColor = useColorModeValue("gray.700", "white");

  const { user } = useAuth();
  const {
    kycVerified,
    canProceed,
    usedFreeTrade,
    loading,
    freeTrades,
    tradesThisCycle,
    quotaExceeded,
  } = useQuota();

  const [updatedListing, setUpdatedListing] = useState({});
  const [reason, setReason] = useState("");
  const [isSeller, setIsSeller] = useState(false);
  const [canEditCancel, setCanEditCancel] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [sellerInfo, setSellerInfo] = useState(null);


// ✅ Fetch user & seller profiles
useEffect(() => {
  if (!listing || !user) return;

  setIsSeller(user.uid === listing.userId);
  setCanEditCancel(user.uid === listing.userId && listing.status === "active");
  setUpdatedListing(listing);

  const fetchUserAndSellerProfiles = async () => {
    try {
      const fetchedUser = await fetchUser(user.uid);
      if (fetchedUser) setUserInfo(fetchedUser);
    } catch (error) {
      toast({
        title: t("listingdetailsmodal.user_profile_fetch_error_title"),
        description:
          error.message || t("listingdetailsmodal.user_profile_fetch_error_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    if (!listing?.userId) return;

    try {
      const fetchedSeller = await fetchUser(listing.userId);
      if (fetchedSeller) {
        setSellerInfo(fetchedSeller);
      } else {
        toast({
          title: t("listingdetailsmodal.seller_profile_fetch_error_title"),
          description: "Seller profile not found.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: t("listingdetailsmodal.seller_profile_fetch_error_title"),
        description: error.message || t("listingdetailsmodal.seller_profile_fetch_error_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  fetchUserAndSellerProfiles();
}, [listing, user, toast]);

  if (!listing) return null;

  const calculateEquivalent = () => {
    const amount = parseFloat(listing.offeredAmount);
    const rate = parseFloat(listing.rate);
    if (isNaN(amount) || isNaN(rate)) return `N/A`;
    return `${(amount * rate).toLocaleString()} ${listing.desiredCurrency || "XAF"}`;
  };

  const handleContactSeller = async () => {
    console.log("Starting quota enforcement check for user:", user?.uid);
    if (!user) {
      console.warn("No user detected, cannot proceed with contacting seller");
      return toast({
        title: t("listingdetailsmodal.auth_required_title"),
        description: t("listingdetailsmodal.auth_required_message"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }

    if (loading) {
      return toast({
        title: "Checking KYC & Quota...",
        description: "Please wait while we verify your account.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    }

    if (!kycVerified) {
      return toast({
        title: "KYC Verification Required",
        description: "You must complete KYC verification before contacting a seller.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }

    if (!canProceed) {
      return toast({
        title: "Quota Limit Reached",
        description: "You have reached your transaction limit.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } else {
      console.log("tradesThisCycle:", tradesThisCycle);
      await updateDoc(doc(firestore, "users", user.uid), {
        tradesThisCycle: tradesThisCycle - 1,
      });
    }

    if (usedFreeTrade) {
      console.log("freeTrades:", freeTrades);
      await updateDoc(doc(firestore, "users", user.uid), {
        freeTrades: freeTrades - 1,
      });

      toast({
        title: "Free Trade Used!",
        description: "Your free trade has been applied. Remaining free trades updated.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
    }

    if (!listing || !listing.id || !listing.amount || !listing.rate) {
      console.error("Invalid listing data detected", listing);
      return toast({
        title: t("listingdetailsmodal.invalid_trade_title"),
        description: "Listing data is incomplete. Please contact support if the issue persists.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }

    try {
      console.log("Attempting to create chat for listing", listing.id);
      const chatRef = collection(firestore, "chats");
      const q = query(chatRef, where("buyerId", "==", user.uid), where("listingId", "==", listing.id));

      const chatSnapshot = await getDocs(q);
      console.log("Chat snapshot found:", chatSnapshot.docs.length);
      if (!chatSnapshot.empty) {
        console.warn("Chat already exists for this listing and user");
        navigate(`/personal/chat/${chatSnapshot.docs[0].id}`);
        return;
      }

      const batch = writeBatch(firestore);

      // Create chat document
      const newChatRef = doc(collection(firestore, "chats"));
      batch.set(newChatRef, {
        buyerId: user.uid,
        sellerId: listing.userId,
        listingId: listing.id,
        lastMessage: "",
        lastMessageAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        status: "initiated",
      });

      // Update listing status
      const listingDocRef = doc(firestore, "listings", listing.id);
      batch.update(listingDocRef, { status: "matched" });
      console.log("Listing status update scheduled");

      // Create trade record
      const tradesRef = doc(collection(firestore, "trades"));
      batch.set(tradesRef, {
        buyerId: user.uid,
        sellerId: listing.userId,
        listingId: listing.id,
        amount: listing.amount,
        offeredCurrency: listing.offeredCurrency,
        requestedCurrency: listing.desiredCurrency,
        rate: listing.rate,
        paymentMethods: listing.paymentMethods,
        location: listing.location.town,
        status: "initiated",
        createdAt: serverTimestamp(),
        lastUpdatedAt: null,
        completedAt: null,
        disputeDetails: null,
      });
      console.log("Trade creation scheduled");

      // Create transaction record
      const transactionsRef = doc(collection(firestore, "transactions"));
      batch.set(transactionsRef, {
        userId: user.uid,
        listingId: listing.id,
        amount: listing.amount,
        offeredCurrency: listing.offeredCurrency,
        requestedCurrency: listing.desiredCurrency,
        rate: listing.rate,
        status: "initiated",
        createdAt: serverTimestamp(),
        lastUpdatedAt: null,
        completedAt: null,
      });
      console.log("Transaction creation scheduled");

      await batch.commit();
      console.log("Batch operations completed successfully");

      // Fetch the seller's phone number
      const sellerProfile = await fetchUserProfile(listing.userId);
      if (sellerProfile?.phoneNumber) {
        console.log("Sending notification message to seller at phone number:", sellerProfile.phoneNumber);
        await addDoc(collection(firestore, "messages"), {
          From: "+14388072347",
          to: sellerProfile.phoneNumber,
          body: `Hello ${sellerProfile.name}, your listing for ${listing.amount} ${listing.offeredCurrency} has a new match! Please check your PeerX dashboard for more details.`,
        });
      }

      toast({
        title: t("listingdetailsmodal.chat_initiated_title"),
        description: t("listingdetailsmodal.chat_initiated_message"),
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      navigate(`/personal/chat/${newChatRef.id}`);

    } catch (error) {
      console.error("Error contacting seller:", error);
      toast({
        title: t("listingdetailsmodal.contact_seller_error_title"),
        description: `Failed to initiate contact: ${error.message}`,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };


  const handleUpdateListing = async () => {
    try {
      const listingDocRef = doc(firestore, "listingss", listing.id);
      await updateDoc(listingDocRef, {
        ...updatedListing,
      });
      toast({
        title: "Listing Updated",
        description: "The listing details have been successfully updated.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error updating listing:", error);
      toast({
        title: "Error",
        description: "An error occurred while updating the listing.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleCancelListing = async () => {
    try {
      const listingDocRef = doc(firestore, "listings", listing.id);
      await updateDoc(listingDocRef, {
        status: "archived",
        cancellationReason: reason,
      });
      toast({
        title: "Listing Canceled",
        description: "The listing has been successfully canceled.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error("Error canceling listing:", error);
      toast({
        title: "Error",
        description: "An error occurred while canceling the listing.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg={modalBg} color={textColor}> {/* Apply background and color to ModalContent */}
        <ModalHeader>{t("listingdetailsmodal.user_actions_header")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs isFitted variant="line">
            <TabList>
              <Tab>{t("listingdetailsmodal.view_details_tab")}</Tab>
              <Tab isDisabled={!canEditCancel}>{t("listingdetailsmodal.edit_listing_tab")}</Tab>
              <Tab isDisabled={!canEditCancel}>{t("listingdetailsmodal.cancel_listing_tab")}</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
              {!sellerInfo ? (
                  <Text>Loading seller info...</Text>
                ) : (
                  <>
                <Text as='em'>{sellerInfo.userName}</Text>
                <Text fontWeight="bold" my={2}>
                  {t("listingdetailsmodal.amount_label")}: {listing.offeredAmount || "N/A"} {listing.offeredCurrency || "CAD"}
                </Text>
                <Text mb={2}>
                  {t("listingdetailsmodal.exchange_rate_label")}: {listing.rate || "N/A"} (
                  {listing.desiredCurrency || "XAF"}/{listing.offeredCurrency || "CAD"})
                </Text>
                <Text mb={2}>{t("listingdetailsmodal.location_label")}: {listing.location.town || "N/A"}</Text>
                <Text mb={2}>
                  {t("listingdetailsmodal.payment_methods_label")}: {Array.isArray(listing.paymentMethods) ? listing.paymentMethods.join(", ") : listing.paymentMethods || "N/A"}
                </Text>
                <Text mb={2}>{t("listingdetailsmodal.approx_equiv_label")}: {calculateEquivalent()}</Text>
                <Text mb={2}>
                  {t("listingdetailsmodal.status_label")}: <span style={{ fontWeight: "bold" }}>
                    {listing.status === "matched"
                      ? t("listingdetailsmodal.under_negotiation_status")
                      : listing.status || t("listingdetailsmodal.na_placeholder")}
                  </span>
                </Text>
                <Text mb={2}>
                  {t("listingdetailsmodal.created_at_label")}:{" "}
                  {listing.createdAt ? new Date(listing.createdAt.seconds * 1000).toLocaleString() : t("listingdetailsmodal.na_placeholder")}
                </Text>
                <Button
                  mt={4}
                  colorScheme="blue"
                  onClick={handleContactSeller}
                  isDisabled={isSeller || listing.status === "matched" || !kycVerified || loading || quotaExceeded}
                >
                  {t("listingdetailsmodal.contact_seller_button")}
                </Button>
                {quotaExceeded && (
                  <Text mt={2} color="red.500" fontSize="sm">
                    {t("listingdetailsmodal.quota_exceeded_warning")}
                  </Text>
                )}
                {isSeller && (
                  <Text mt={2} color="gray.500" fontSize="sm">
                    {t("listingdetailsmodal.seller_contact_error")}
                  </Text>
                )}
                </>
              )}
              </TabPanel>
              <TabPanel>
                {canEditCancel ? (
                  <>
                    <FormControl mb={4}>
                      <FormLabel>Amount</FormLabel>
                      <Input
                        placeholder="Amount"
                        value={updatedListing.amount || ""}
                        onChange={(e) =>
                          setUpdatedTrade({ ...updatedListing, amount: e.target.value })
                        }
                      />
                    </FormControl>
                    <FormControl mb={4}>
                      <FormLabel>Exchange Rate</FormLabel>
                      <Input
                        placeholder="Exchange Rate"
                        value={updatedListing.rate || ""}
                        onChange={(e) =>
                          setUpdatedTrade({ ...updatedListing, rate: e.target.value })
                        }
                      />
                    </FormControl>
                    <FormControl mb={4}>
                      <FormLabel>Location</FormLabel>
                      <Input
                        placeholder="Location"
                        value={updatedListing.location.town || ""}
                        onChange={(e) =>
                          setUpdatedTrade({ ...updatedListing, location: e.target.value })
                        }
                      />
                    </FormControl>
                    <FormControl mb={4}>
                      <FormLabel>Payment Methods</FormLabel>
                      <Textarea
                        placeholder="Comma-separated payment methods"
                        value={
                          Array.isArray(updatedListing.paymentMethods)
                            ? updatedListing.paymentMethods.join(", ")
                            : updatedListing.paymentMethods || ""
                        }
                        onChange={(e) =>
                          setUpdatedListing({
                            ...updatedListing,
                            paymentMethods: e.target.value.split("listingdetailsmodal.,").map((method) => method.trim()),
                          })
                        }
                      />
                    </FormControl>
                    <Button colorScheme="blue" onClick={handleUpdateListing}>
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <Text fontWeight="bold" color="red.400">
                    You are not authorized to edit this listing, or the listing is no longer active.
                  </Text>
                )}
              </TabPanel>
              <TabPanel>
                {canEditCancel ? (
                  <>
                    <FormControl mb={4}>
                      <FormLabel>Reason for Cancellation</FormLabel>
                      <Textarea
                        placeholder="Provide a reason for cancellation (optional)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                    </FormControl>
                    <Button colorScheme="red" onClick={handleCancelListing}>
                      Confirm Cancellation
                    </Button>
                  </>
                ) : (
                  <Text fontWeight="bold" color="red.400">
                    You are not authorized to cancel this listing, or the listing is no longer active.
                  </Text>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="gray" onClick={onClose}>
            {t("listingdetailsmodal.close_button")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ListingDetailsModal;
